import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Loader2, TrendingDown, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

export default function DiagnosticResultsPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedType, setExpandedType] = useState(null);

  useEffect(() => {
    fetchResults();
  }, [sessionId]);

  const fetchResults = async () => {
    try {
      const data = await api.diagnosticGetSession(sessionId);
      if (!data.submitted_at) {
        navigate(`/diagnostic/take/${sessionId}`);
        return;
      }
      setSession(data);
    } catch (error) {
      console.error('Failed to load results:', error);
      toast.error('Failed to load results');
      navigate('/diagnostic');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!session?.results) {
    return null;
  }

  const { results } = session;
  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (percentage) => {
    if (percentage >= 80) return 'bg-green-50 border-green-200';
    if (percentage >= 60) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/diagnostic')}
            className="rounded-full"
            data-testid="diagnostic-results-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Diagnostics
          </Button>
          <Button
            onClick={() => navigate('/diagnostic')}
            className="bg-emerald-600 hover:bg-emerald-700 rounded-full"
            data-testid="diagnostic-new-test-button"
          >
            Start New Test
          </Button>
        </div>

        {/* Overall Score */}
        <Card className={`border-2 ${getScoreBg(results.overall_percentage)}`} data-testid="diagnostic-overall-score-card">
          <CardContent className="p-8 text-center">
            <div className={`text-6xl font-bold ${getScoreColor(results.overall_percentage)} mb-2`}>
              {results.overall_percentage}%
            </div>
            <div className="text-xl font-semibold text-gray-900 mb-1">
              Overall Score
            </div>
            <div className="text-gray-600">
              {results.total_correct} out of {results.total_questions} correct
            </div>
          </CardContent>
        </Card>

        {/* Weakest Areas */}
        {results.weakest_areas && results.weakest_areas.length > 0 && (
          <Card className="border-2 border-orange-200 bg-orange-50">
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-orange-600" />
                <CardTitle className="text-orange-900">Areas Needing Improvement</CardTitle>
              </div>
              <CardDescription>Focus on these topics to boost your score</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-3">
                {results.weakest_areas.map((area, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-lg border border-orange-200">
                    <div className="text-sm font-semibold text-gray-900 mb-1">
                      {area.type_name}
                    </div>
                    <div className={`text-2xl font-bold ${getScoreColor(area.percentage)}`}>
                      {area.percentage}%
                    </div>
                    <div className="text-xs text-gray-500">
                      {area.correct}/{area.total} correct
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Score Breakdown by Type */}
        <Card>
          <CardHeader>
            <CardTitle data-testid="diagnostic-breakdown-title">Detailed Breakdown</CardTitle>
            <CardDescription>Click to see individual questions and explanations</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {results.type_scores.map((score, idx) => {
              const typeData = results.by_type[score.type];
              const isExpanded = expandedType === score.type;
              
              return (
                <div key={score.type} className="border rounded-lg overflow-hidden">
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedType(isExpanded ? null : score.type)}
                    data-testid={`diagnostic-type-${idx}`}
                  >
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{score.type_name}</div>
                      <div className="text-sm text-gray-500">
                        {score.correct}/{score.total} correct
                      </div>
                    </div>
                    <div className={`text-2xl font-bold ${getScoreColor(score.percentage)}`}>
                      {score.percentage}%
                    </div>
                  </div>
                  
                  {isExpanded && typeData && (
                    <div className="border-t bg-gray-50 p-4 space-y-4">
                      {typeData.questions.map((q, qIdx) => (
                        <div key={qIdx} className="bg-white p-4 rounded border space-y-2">
                          <div className="flex items-start gap-2">
                            {q.is_correct ? (
                              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                            )}
                            <div className="flex-1 space-y-2">
                              <div className="font-medium text-gray-900">
                                Question {qIdx + 1}
                              </div>
                              <div className="text-sm text-gray-700 whitespace-pre-wrap">
                                {q.question_text}
                              </div>
                              <div className="text-sm space-y-1">
                                <div className={q.is_correct ? 'text-green-700' : 'text-red-700'}>
                                  <span className="font-semibold">Your answer:</span> {q.user_answer || '(no answer)'}
                                </div>
                                {!q.is_correct && (
                                  <div className="text-green-700">
                                    <span className="font-semibold">Correct answer:</span> {q.correct_answer}
                                  </div>
                                )}
                              </div>
                              {q.explanation && (
                                <div className="text-sm bg-blue-50 p-3 rounded border border-blue-200">
                                  <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                                    <div className="text-blue-900">{q.explanation}</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
